import { Card, Table, Tag, Button, Space, message, Drawer, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, Report } from "../api";

const TYPE_COLOR: Record<string, string> = {
  msg: "blue", post: "purple", user: "orange", group: "geekblue", dapp: "magenta",
};

type ReportWithContent = Report & { target_content?: string | null };

export default function Reports() {
  const qc = useQueryClient();
  const [detail, setDetail] = useState<ReportWithContent | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["reports"],
    queryFn: async () => (await api.get<{ items: ReportWithContent[] }>("/reports")).data.items,
    refetchInterval: 60_000,
  });

  const handle = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: string; note?: string }) =>
      api.post(`/reports/${id}/handle`, { action, note }),
    onSuccess: () => {
      message.success("已处理");
      qc.invalidateQueries({ queryKey: ["reports"] });
      setDetail(null);
    },
  });

  return (
    <>
      <Card
        style={{ background: "#10131A", border: "1px solid #1A1D26" }}
        title={<span style={{ color: "#F5F5F7" }}>举报队列</span>}
      >
        <Table<ReportWithContent>
          rowKey="id"
          loading={isLoading}
          dataSource={data}
          pagination={{ pageSize: 20 }}
          onRow={(r) => ({ onClick: () => setDetail(r), style: { cursor: "pointer" } })}
          columns={[
            {
              title: "类型", dataIndex: "target_type", width: 90,
              render: (v: string) => <Tag color={TYPE_COLOR[v] ?? "default"}>{v}</Tag>,
            },
            {
              title: "目标", dataIndex: "target_id",
              render: (v: string) => <span style={{ fontFamily: "JetBrains Mono", fontSize: 12 }}>{v.slice(0, 12)}…</span>,
            },
            {
              title: "被举报内容", dataIndex: "target_content",
              ellipsis: true,
              render: (v: string | null) => v ? <span style={{ color: "#F5F5F7" }}>{v}</span> : <span style={{ color: "#6B7280" }}>—</span>,
            },
            { title: "原因", dataIndex: "reason", width: 120 },
            { title: "提交时间", dataIndex: "created_at", width: 180,
              render: (v) => new Date(v).toLocaleString() },
            {
              title: "操作", width: 220,
              render: (_, r) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button size="small" type="primary" danger
                    onClick={() => handle.mutate({ id: r.id, action: "remove" })}>
                    移除内容
                  </Button>
                  <Button size="small"
                    onClick={() => handle.mutate({ id: r.id, action: "warn" })}>
                    警告
                  </Button>
                  <Button size="small"
                    onClick={() => handle.mutate({ id: r.id, action: "dismiss" })}>
                    驳回
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title="举报详情"
        open={!!detail}
        onClose={() => setDetail(null)}
        width={480}
        styles={{ body: { background: "#10131A" } }}
      >
        {detail && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div><Tag color={TYPE_COLOR[detail.target_type] ?? "default"}>{detail.target_type}</Tag></div>
            <Typography.Paragraph style={{ color: "#A1A1AA", fontSize: 12 }}>
              目标 ID：{detail.target_id}
            </Typography.Paragraph>
            {detail.target_content && (
              <div style={{ padding: 12, background: "#1A1D26", borderRadius: 8, border: "1px solid #2A2D36" }}>
                <div style={{ color: "#6B7280", fontSize: 11, marginBottom: 6 }}>被举报内容</div>
                <Typography.Text style={{ color: "#F5F5F7", whiteSpace: "pre-wrap" }}>
                  {detail.target_content}
                </Typography.Text>
              </div>
            )}
            {detail.reason && <Typography.Text style={{ color: "#A1A1AA" }}>原因：{detail.reason}</Typography.Text>}
            {detail.detail && <Typography.Paragraph style={{ color: "#A1A1AA" }}>{detail.detail}</Typography.Paragraph>}
          </Space>
        )}
      </Drawer>
    </>
  );
}
